import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { 
  FiBriefcase, FiCalendar, FiClock, FiCheckCircle, 
  FiXCircle, FiFileText, FiExternalLink, FiLoader, FiTrash2, FiMic, FiZap, FiInfo
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { applicationApi } from '../../services/api';
import ApplicationDetailModal from '../../components/applications/ApplicationDetailModal';
import ApplicationCard from '../../components/applications/ApplicationCard';
import ApplicationViewTabs from '../../components/applications/ApplicationViewTabs';
import FilterBar from '../../components/applications/FilterBar';
import Tooltip from '../../components/Tooltip';
import ListView from '../../components/applications/ListView';
import StatsView from '../../components/applications/StatsView';
import CalendarView from '../../components/applications/CalendarView';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';
import { useCache } from '../../contexts/CacheContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { FeedbackBanner, FeedbackQuestionnaire } from '../../components/feedback';

// Define status configuration
const statusConfig = [
  { id: 'applied', name: 'Applied', icon: FiClock, color: 'blue' },
  { id: 'interviewing', name: 'Interviewing', icon: FiClock, color: 'yellow' },
  { id: 'offered', name: 'Offered', icon: FiCheckCircle, color: 'green' },
  { id: 'rejected', name: 'Rejected', icon: FiXCircle, color: 'red' },
];

// Static color maps — Tailwind v3 requires full class names at build time;
// dynamic `bg-${color}-50` strings are not included in the CSS bundle.
const columnColorMap = {
  blue:   { dragBg: 'bg-blue-50',   headerBg: 'bg-blue-100',   border: 'border-blue-200',   icon: 'text-blue-500',   title: 'text-blue-700'   },
  yellow: { dragBg: 'bg-yellow-50', headerBg: 'bg-yellow-100', border: 'border-yellow-200', icon: 'text-yellow-500', title: 'text-yellow-700' },
  green:  { dragBg: 'bg-green-50',  headerBg: 'bg-green-100',  border: 'border-green-200',  icon: 'text-green-500',  title: 'text-green-700'  },
  red:    { dragBg: 'bg-red-50',    headerBg: 'bg-red-100',    border: 'border-red-200',    icon: 'text-red-500',    title: 'text-red-700'    },
};

// StatusColumn Component
const StatusColumn = ({ title, icon: Icon, color, status, applications, onRefresh, highlightedApplicationId }) => {
  const colors = columnColorMap[color] || columnColorMap.blue;
  return (
    <Droppable droppableId={status}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`flex flex-col min-h-[500px] w-full ${snapshot.isDraggingOver ? colors.dragBg : 'bg-gray-50'}`}
        >
          <div className={`px-4 py-2 ${colors.headerBg} rounded-t-md border-b ${colors.border} flex items-center`}>
            <Icon className={`h-5 w-5 ${colors.icon} mr-2`} />
            <h3 className={`${colors.title} font-medium`}>{title}</h3>
            <span className="ml-auto bg-white text-gray-600 px-2 py-0.5 rounded-full text-xs">
              {applications.length}
            </span>
          </div>
          
          <div className="flex-1 p-1 space-y-2 overflow-y-auto">
            {applications.length > 0 ? (
              applications.map((app, index) => (
                <Draggable key={app.id} draggableId={app.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`${snapshot.isDragging ? 'opacity-70' : ''} ${
                        highlightedApplicationId === app.id ? 'animate-pulse' : ''
                      }`}
                    >
                      <ApplicationCard 
                        application={app}
                        index={index}
                        refreshApplications={onRefresh}
                        isHighlighted={highlightedApplicationId === app.id}
                      />
                    </div>
                  )}
                </Draggable>
              ))
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-gray-500">No applications</p>
              </div>
            )}
            {provided.placeholder}
          </div>
        </div>
      )}
    </Droppable>
  );
};

// Main ApplicationTracking Component
const ApplicationTracking = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [activeView, setActiveView] = useState('kanban');
  const [highlightedApplicationId, setHighlightedApplicationId] = useState(null);
  const [filters, setFilters] = useState({
    dateFilter: 'all',
    statusFilter: []
  });
  const [showRejectOldAppliedModal, setShowRejectOldAppliedModal] = useState(false);
  const [rejectOldAppliedDaysInput, setRejectOldAppliedDaysInput] = useState('14');
  const [rejectingOldApplied, setRejectingOldApplied] = useState(false);
  const { getApplications, invalidateApplicationCache } = useCache();
  const { currentUser } = useAuth();
  const { shouldShowFeedback, hideFeedbackNotification, checkFeedbackStatus } = useFeedback();
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    fetchApplications();
    checkFeedbackStatus(); // Check feedback status on page load
  }, [checkFeedbackStatus]);
  
  // Refresh feedback status when applications change
  useEffect(() => {
    checkFeedbackStatus();
  }, [applications.length, checkFeedbackStatus]);

  // Derive filtered applications directly — no separate state or effect needed
  const filteredApplications = useMemo(() => {
    let filtered = applications;

    if (filters.dateFilter !== 'all') {
      const daysMap = { '7days': 7, '30days': 30, '90days': 90 };
      const days = daysMap[filters.dateFilter];
      if (days) {
        const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(app => new Date(app.appliedAt) >= cutoff);
      }
    }

    if (filters.statusFilter.length > 0) {
      filtered = filtered.filter(app => filters.statusFilter.includes(app.status));
    }

    return filtered;
  }, [applications, filters]);

  const fetchApplications = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const applications = await getApplications(forceRefresh);
      
      const formattedApplications = applications.map(app => ({
        id: app.id,
        ...app,
        round: typeof app.round === 'number' ? app.round : 0,
        coverLetterId: app.coverLetterId || null,
        onDelete: handleDeleteClick,
        onView: handleViewClick,
        onRoundChange: handleRoundChange,
      }));
      
      setApplications(formattedApplications);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  // Persist a round change for an interviewing application. Updates local
  // state optimistically; on failure, rethrows so the card can roll back.
  const handleRoundChange = async (application, newRound) => {
    const round = Math.max(1, parseInt(newRound, 10) || 1);
    setApplications(prev => prev.map(app => (
      app.id === application.id ? { ...app, round } : app
    )));
    try {
      await applicationApi.updateApplication(application.id, { round });
      invalidateApplicationCache();
    } catch (error) {
      console.error('Error updating interview round:', error);
      toast.error('Failed to update interview round');
      setApplications(prev => prev.map(app => (
        app.id === application.id ? { ...app, round: application.round } : app
      )));
      throw error;
    }
  };
  
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }
    
    const appIndex = applications.findIndex(app => app.id === draggableId);
    if (appIndex === -1) return;
    
    const application = applications[appIndex];
    const newStatus = destination.droppableId;
    
    if (application.status === newStatus) {
      return;
    }
    
    // Mirror the backend's auto-jump: when entering `interviewing` and the
    // current round is 0, bump it to 1 so the card UI matches what the
    // server will store.
    const optimisticRound = (
      newStatus === 'interviewing' && (application.round || 0) < 1
        ? 1
        : application.round
    );

    const updatedApplications = [...applications];
    updatedApplications[appIndex] = {
      ...application,
      status: newStatus,
      round: optimisticRound,
    };
    
    setApplications(updatedApplications);
    
    try {
      setUpdatingStatus(true);
      
      await applicationApi.updateApplication(application.id, { status: newStatus });
      
      // Invalidate the application cache since we've updated the status
      invalidateApplicationCache();
      
      toast.success(`Application moved to ${statusConfig.find(s => s.id === newStatus).name}`);
    } catch (error) {
      console.error('Error updating application status:', error);
      toast.error('Failed to update application status');
      
      setApplications([...applications]);
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  const handleViewClick = (application) => {
    setSelectedApplication(application);
    setDetailModalOpen(true);
  };
  
  const handleDeleteClick = (application) => {
    setApplicationToDelete(application);
    setDeleteModalOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!applicationToDelete) return;
    
    try {
      await applicationApi.deleteApplication(applicationToDelete.id);
      
      setApplications(applications.filter(app => app.id !== applicationToDelete.id));
      
      // Invalidate the application cache
      invalidateApplicationCache();
      
      toast.success("Application deleted successfully");
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting application:", error);
      toast.error("Failed to delete application");
    }
  };
  
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  // Parse days from input (default 14, min 1) for use in eligibility and confirm
  const getRejectOldAppliedDaysParsed = () => Math.max(1, parseInt(rejectOldAppliedDaysInput, 10) || 14);

  // Applications eligible for "reject old applied": status applied and no response for X days (valid appliedAt only)
  const getEligibleApplicationsToReject = (days) => {
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    return applications.filter((app) => {
      if (app.status !== 'applied') return false;
      const appliedAt = app.appliedAt;
      if (!appliedAt) return false;
      const appliedMs = new Date(appliedAt).getTime();
      if (Number.isNaN(appliedMs)) return false;
      return appliedMs < cutoff;
    });
  };

  const handleRejectOldApplied = async () => {
    const days = getRejectOldAppliedDaysParsed();
    const eligible = getEligibleApplicationsToReject(days);
    if (eligible.length === 0) return;
    setRejectingOldApplied(true);
    const BATCH = 5;
    let updated = 0;
    try {
      for (let i = 0; i < eligible.length; i += BATCH) {
        const batch = eligible.slice(i, i + BATCH);
        await Promise.all(batch.map(app => applicationApi.updateApplication(app.id, { status: 'rejected' })));
        updated += batch.length;
      }
      invalidateApplicationCache();
      await fetchApplications(true);
      setShowRejectOldAppliedModal(false);
      toast.success(`${updated} application(s) marked as rejected`);
    } catch (error) {
      console.error('Error rejecting old applications:', error);
      toast.error('Failed to update some applications');
    } finally {
      setRejectingOldApplied(false);
    }
  };

  const highlightTimeoutsRef = useRef([]);

  // When landing with ?highlight=, force refresh so the newly tracked application shows immediately
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setHighlightedApplicationId(highlightId);
      fetchApplications(true);
      const t1 = setTimeout(() => {
        navigate('/applications', { replace: true });
        const t2 = setTimeout(() => setHighlightedApplicationId(null), 5000);
        highlightTimeoutsRef.current.push(t2);
      }, 500);
      highlightTimeoutsRef.current = [t1];
    }
    return () => {
      highlightTimeoutsRef.current.forEach(id => clearTimeout(id));
      highlightTimeoutsRef.current = [];
    };
  }, [location.search, navigate]);

  // Group applications by status (memoized to avoid re-filtering on every render)
  const groupedApplications = useMemo(() => ({
    applied: filteredApplications.filter(app => app.status === 'applied'),
    interviewing: filteredApplications.filter(app => app.status === 'interviewing'),
    offered: filteredApplications.filter(app => app.status === 'offered'),
    rejected: filteredApplications.filter(app => app.status === 'rejected'),
  }), [filteredApplications]);
  
  if (loading) {
    return <LoadingSpinner message="Loading applications..." />;
  }

  return (
    <div className="p-6">
      <div className="min-w-0">
        <PageHeader
          title={pageDescriptions['/applications'].title}
          description={pageDescriptions['/applications'].description}
          whatYouCanDo={pageDescriptions['/applications'].whatYouCanDo}
          whatYouShouldDo={pageDescriptions['/applications'].whatYouShouldDo}
          actions={
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => {
                  setRejectOldAppliedDaysInput('14');
                  setShowRejectOldAppliedModal(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <FiXCircle className="mr-2 h-4 w-4" />
                Reject no response
                <span
                  className="ml-1.5 inline-flex text-gray-400 hover:text-gray-600"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  role="img"
                  aria-label="Info"
                >
                  <Tooltip
                    position="bottom"
                    wrap
                    wide
                    text="Mark as Rejected all applications still in Applied that have had no response for the number of days below."
                  >
                    <FiInfo className="h-4 w-4" />
                  </Tooltip>
                </span>
              </button>
              <Link
                to="/jobs/mock-interview"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <FiMic className="mr-2 h-4 w-4" />
                Mock Interview
              </Link>
              <Link
                to="/jobs"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <FiBriefcase className="mr-2 h-4 w-4" />
                Browse Jobs
              </Link>
            </div>
          }
        />

        <ApplicationViewTabs activeView={activeView} setActiveView={setActiveView} />

        <FilterBar onApplyFilters={handleApplyFilters} />

        {/* Remind user to set auto-cleanup in Settings */}
        {currentUser && !currentUser.auto_cleanup_enabled && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-4 flex items-center justify-between flex-wrap gap-2 mb-4">
            <p className="text-sm text-amber-800">
              You can automatically remove old saved jobs and mark old applications as Rejected each time you log in. Set it up in Settings.
            </p>
            <Link
              to="/settings"
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-amber-800 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
            >
              Open Settings
            </Link>
          </div>
        )}
        
        {/* Feedback Banner */}
        {shouldShowFeedback && (
          <FeedbackBanner onOpenQuestionnaire={() => setShowQuestionnaire(true)} />
        )}
        
        {filteredApplications.length > 0 ? (
          <>
            {activeView === 'kanban' && (
              <DragDropContext onDragEnd={handleDragEnd}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <StatusColumn
                    title="Applied"
                    icon={FiClock}
                    color="blue"
                    status="applied"
                    applications={groupedApplications.applied}
                    statusConfig={statusConfig}
                    onRefresh={fetchApplications}
                    highlightedApplicationId={highlightedApplicationId}
                  />
                  
                  <StatusColumn
                    title="Interviewing"
                    icon={FiClock}
                    color="yellow"
                    status="interviewing"
                    applications={groupedApplications.interviewing}
                    statusConfig={statusConfig}
                    onRefresh={fetchApplications}
                    highlightedApplicationId={highlightedApplicationId}
                  />
                  
                  <StatusColumn
                    title="Offered"
                    icon={FiCheckCircle}
                    color="green"
                    status="offered"
                    applications={groupedApplications.offered}
                    statusConfig={statusConfig}
                    onRefresh={fetchApplications}
                    highlightedApplicationId={highlightedApplicationId}
                  />
                  
                  <StatusColumn
                    title="Rejected"
                    icon={FiXCircle}
                    color="red"
                    status="rejected"
                    applications={groupedApplications.rejected}
                    statusConfig={statusConfig}
                    onRefresh={fetchApplications}
                    highlightedApplicationId={highlightedApplicationId}
                  />
                </div>
              </DragDropContext>
            )}
            
            {activeView === 'list' && (
              <ListView 
                applications={filteredApplications} 
                onRefresh={fetchApplications}
                highlightedApplicationId={highlightedApplicationId}
              />
            )}
            
            {activeView === 'stats' && (
              <StatsView applications={filteredApplications} />
            )}
            
            {activeView === 'calendar' && (
              <CalendarView applications={filteredApplications} />
            )}
          </>
        ) : (
          <div className="text-center py-12 card">
            <FiBriefcase className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No job applications found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {applications.length > 0 
                ? "No applications match your current filters."
                : "Start by browsing jobs and tracking your applications."}
            </p>
            <div className="mt-6">
              {applications.length > 0 ? (
                <button
                  onClick={() => setFilters({ dateFilter: 'all', statusFilter: [] })}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Clear Filters
                </button>
              ) : (
                <Link
                  to="/jobs"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <FiBriefcase className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  Browse Jobs
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Status update indicator */}
      {updatingStatus && (
        <div className="fixed bottom-4 right-4 bg-primary-600 text-white px-4 py-2 rounded-md shadow-lg flex items-center">
          <FiLoader className="animate-spin mr-2 h-4 w-4" />
          Updating status...
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteModalOpen && applicationToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiTrash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Application</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this application? This action cannot be undone.
                      </p>
                      <p className="text-sm font-medium text-gray-900 mt-2">
                        {applicationToDelete.company} - {applicationToDelete.jobTitle}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setDeleteModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject old applied (no response) modal */}
      {showRejectOldAppliedModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiZap className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Reject applications with no response</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Mark as Rejected all applications still in &quot;Applied&quot; that have had no response for the number of days below.
                    </p>
                    <div className="mt-4">
                      <label htmlFor="reject-old-days" className="block text-sm font-medium text-gray-700">
                        No response for (days)
                      </label>
                      <input
                        id="reject-old-days"
                        type="number"
                        min={1}
                        value={rejectOldAppliedDaysInput}
                        onChange={(e) => setRejectOldAppliedDaysInput(e.target.value)}
                        className="mt-1 block w-24 rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <p className="mt-3 text-sm text-gray-600">
                      {getEligibleApplicationsToReject(getRejectOldAppliedDaysParsed()).length === 0
                        ? 'No applications match.'
                        : `${getEligibleApplicationsToReject(getRejectOldAppliedDaysParsed()).length} application(s) will be marked as rejected.`}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={getEligibleApplicationsToReject(getRejectOldAppliedDaysParsed()).length === 0 || rejectingOldApplied}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleRejectOldApplied}
                >
                  {rejectingOldApplied ? 'Updating…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  disabled={rejectingOldApplied}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={() => setShowRejectOldAppliedModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Application Detail Modal */}
      {detailModalOpen && selectedApplication && (
        <ApplicationDetailModal
          isOpen={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          application={selectedApplication}
        />
      )}
      
      {/* Feedback Questionnaire */}
      <FeedbackQuestionnaire 
        isOpen={showQuestionnaire} 
        onClose={() => {
          setShowQuestionnaire(false);
          hideFeedbackNotification();
        }} 
      />

    </div>
  );
};

export default ApplicationTracking;